import type { EnemyKind, SpawnPlan, WeaponKind } from "./game";

export const CAMPAIGN_VERSION = 1;
export const CAMPAIGN_LEVEL_COUNT = 10;
export const CAMPAIGN_FINALE_MINIMUM_DURATION_MS = 45_000;
export const CAMPAIGN_CLEAR_SETTLE_MS = 50;
export const CAMPAIGN_TRANSITION_MS = 900;
export const CAMPAIGN_CONTINUATION_FIRST_SPAWN_MS = 900;
export const CAMPAIGN_MANUAL_FIRST_SPAWN_MS = 2_000;

export interface CampaignPalette {
  readonly top: number;
  readonly bottom: number;
  readonly accent: number;
  readonly danger: number;
}

export interface CampaignLevel {
  readonly id: number;
  readonly name: string;
  readonly shortName: string;
  readonly description: string;
  readonly intro: string;
  readonly spawnPlan: readonly SpawnPlan[];
  readonly clogTarget: number;
  readonly scoreTarget: number;
  readonly speedMultiplier: number;
  readonly seed: number;
  readonly bonus: boolean;
  readonly bossAt: number | null;
  readonly startingLives: number;
  readonly featuredWeapon: WeaponKind;
  readonly palette: CampaignPalette;
}

const LANES = [92, 181, 270, 359, 448] as const;
const MOTIONS = ["straight", "diagonal", "sway"] as const;

interface PlanOptions {
  readonly count: number;
  readonly start: number;
  readonly gap: number;
  readonly kinds: readonly Exclude<EnemyKind, "boss">[];
  readonly motions: readonly SpawnPlan["motion"][];
  readonly laneStep?: number;
}

function plan(options: PlanOptions): readonly SpawnPlan[] {
  const laneStep = options.laneStep ?? 2;
  return Array.from({ length: options.count }, (_, index) => ({
    at: options.start + index * options.gap,
    kind: options.kinds[index % options.kinds.length] ?? "regular",
    motion: options.motions[index % options.motions.length] ?? "straight",
    lane: LANES[(index * laneStep + options.count) % LANES.length] ?? 270,
  }));
}

export function continuationSpawnPlan(
  spawnPlan: readonly SpawnPlan[],
): readonly SpawnPlan[] {
  const firstSpawnAt = spawnPlan[0]?.at;
  if (firstSpawnAt === undefined) return spawnPlan;
  const shift = Math.max(
    0,
    firstSpawnAt - CAMPAIGN_CONTINUATION_FIRST_SPAWN_MS,
  );
  if (shift === 0) return spawnPlan;
  return spawnPlan.map((spawn) => ({ ...spawn, at: spawn.at - shift }));
}

const palettes = {
  mint: { top: 0x160d2a, bottom: 0x24465a, accent: 0x71e4d0, danger: 0xff6fa9 },
  pink: { top: 0x25113b, bottom: 0x5c2755, accent: 0xff8fbd, danger: 0xffcf5c },
  blue: { top: 0x101c3e, bottom: 0x245a78, accent: 0x8be7ff, danger: 0xff7fae },
  gold: { top: 0x21122d, bottom: 0x5d4325, accent: 0xffcf5c, danger: 0xff7fae },
  darkFlush: {
    top: 0x070511,
    bottom: 0x21103a,
    accent: 0x53f2b8,
    danger: 0xff4fd8,
  },
  shadow: {
    top: 0x03040d,
    bottom: 0x170d2c,
    accent: 0xb263ff,
    danger: 0xff4b87,
  },
  eclipse: {
    top: 0x02030a,
    bottom: 0x21091f,
    accent: 0x70ffc6,
    danger: 0xffcf5c,
  },
} as const;

export const CAMPAIGN_LEVELS: readonly CampaignLevel[] = [
  {
    id: 1,
    name: "Fresh Fixtures",
    shortName: "Fresh",
    description: "Glide, aim, and learn the three-hit clog.",
    intro: "Level 1 · Fresh Fixtures",
    spawnPlan: plan({
      count: 8,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 4_000,
      kinds: ["regular"],
      motions: ["straight"],
    }),
    clogTarget: 6,
    scoreTarget: 600,
    speedMultiplier: 1.05,
    seed: 101,
    bonus: false,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "poop",
    palette: palettes.mint,
  },
  {
    id: 2,
    name: "Sideways Swirl",
    shortName: "Swirl",
    description: "Follow gentle diagonal toilet trails.",
    intro: "Level 2 · Sideways Swirl",
    spawnPlan: plan({
      count: 9,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 3_650,
      kinds: ["regular"],
      motions: ["straight", "diagonal"],
      laneStep: 3,
    }),
    clogTarget: 7,
    scoreTarget: 750,
    speedMultiplier: 1.22,
    seed: 202,
    bonus: false,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "poop",
    palette: palettes.pink,
  },
  {
    id: 3,
    name: "Speedy Seatbelt",
    shortName: "Speedy",
    description: "Meet Speedy Potty one at a time, then in pairs.",
    intro: "Level 3 · Speedy Seatbelt",
    spawnPlan: plan({
      count: 10,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 3_350,
      kinds: ["speedy", "regular", "regular"],
      motions: ["straight", "diagonal"],
      laneStep: 2,
    }),
    clogTarget: 8,
    scoreTarget: 950,
    speedMultiplier: 1.4,
    seed: 303,
    bonus: false,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "bubble",
    palette: palettes.blue,
  },
  {
    id: 4,
    name: "Sturdy Throne Zone",
    shortName: "Sturdy",
    description: "Prioritize armored thrones before they drift too low.",
    intro: "Level 4 · Sturdy Throne Zone",
    spawnPlan: plan({
      count: 10,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 3_200,
      kinds: ["regular", "sturdy", "regular", "speedy"],
      motions: ["straight", "diagonal"],
      laneStep: 3,
    }),
    clogTarget: 8,
    scoreTarget: 1_250,
    speedMultiplier: 1.58,
    seed: 404,
    bonus: false,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "bubble",
    palette: palettes.gold,
  },
  {
    id: 5,
    name: "Bubble Bath Break",
    shortName: "Bath Break",
    description: "A harmless gallery—misses cannot cost a heart.",
    intro: "Level 5 · Bubble Bath Break",
    spawnPlan: plan({
      count: 12,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 3_000,
      kinds: ["speedy", "regular"],
      motions: ["diagonal", "sway", "straight"],
      laneStep: 2,
    }),
    clogTarget: 9,
    scoreTarget: 1_250,
    speedMultiplier: 1.76,
    seed: 505,
    bonus: true,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "bubble",
    palette: palettes.blue,
  },
  {
    id: 6,
    name: "Zigzag Restroom",
    shortName: "Zigzag",
    description: "Read straight, diagonal, and swaying approaches.",
    intro: "Level 6 · Zigzag Restroom",
    spawnPlan: plan({
      count: 12,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 2_850,
      kinds: ["regular", "speedy", "regular", "sturdy"],
      motions: MOTIONS,
      laneStep: 3,
    }),
    clogTarget: 9,
    scoreTarget: 1_400,
    speedMultiplier: 1.94,
    seed: 606,
    bonus: false,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "bubble",
    palette: palettes.mint,
  },
  {
    id: 7,
    name: "Plunger Power",
    shortName: "Plunger",
    description: "The shadow fleet arrives—watch for glowing speed warnings.",
    intro: "Level 7 · Plunger Power · Dark Flush Begins",
    spawnPlan: plan({
      count: 12,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 2_700,
      kinds: ["sturdy", "regular", "speedy"],
      motions: ["straight", "sway", "diagonal"],
      laneStep: 2,
    }),
    clogTarget: 9,
    scoreTarget: 1_650,
    speedMultiplier: 2.12,
    seed: 707,
    bonus: false,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "plunger",
    palette: palettes.darkFlush,
  },
  {
    id: 8,
    name: "Toilet Traffic",
    shortName: "Traffic",
    description: "Navigate a faster shadow fleet crossing busy lanes.",
    intro: "Level 8 · Toilet Traffic",
    spawnPlan: plan({
      count: 15,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 2_450,
      kinds: ["regular", "speedy", "regular", "sturdy"],
      motions: ["diagonal", "straight", "sway"],
      laneStep: 3,
    }),
    clogTarget: 12,
    scoreTarget: 2_050,
    speedMultiplier: 2.3,
    seed: 808,
    bonus: false,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "plunger",
    palette: palettes.shadow,
  },
  {
    id: 9,
    name: "Final Flush Rehearsal",
    shortName: "Rehearsal",
    description: "Read warning flashes through a fast, coordinated gauntlet.",
    intro: "Level 9 · Final Flush Rehearsal",
    spawnPlan: plan({
      count: 15,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 2_300,
      kinds: ["speedy", "sturdy", "regular", "regular"],
      motions: ["sway", "diagonal", "straight"],
      laneStep: 2,
    }),
    clogTarget: 12,
    scoreTarget: 2_250,
    speedMultiplier: 2.48,
    seed: 909,
    bonus: false,
    bossAt: null,
    startingLives: 3,
    featuredWeapon: "plunger",
    palette: palettes.eclipse,
  },
  {
    id: 10,
    name: "Lid Shield Showdown",
    shortName: "Showdown",
    description: "Survive the eclipse fleet and break the faster Lid Shield.",
    intro: "Level 10 · Lid Shield Showdown",
    spawnPlan: plan({
      count: 9,
      start: CAMPAIGN_MANUAL_FIRST_SPAWN_MS,
      gap: 3_800,
      kinds: ["regular", "speedy", "sturdy"],
      motions: ["straight", "diagonal"],
      laneStep: 3,
    }),
    clogTarget: 7,
    scoreTarget: 2_750,
    speedMultiplier: 2.66,
    seed: 1010,
    bonus: false,
    bossAt: 40_000,
    startingLives: 4,
    featuredWeapon: "plunger",
    palette: palettes.eclipse,
  },
] as const;

export function getCampaignLevel(id: number): CampaignLevel {
  return (
    CAMPAIGN_LEVELS.find((level) => level.id === id) ?? CAMPAIGN_LEVELS[0]!
  );
}
